import React, { useCallback, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";

import {
  usePatchEmployerByIdMutation,
  useGetEmployerByIdQuery,
} from "services/employer-api";

import { useSelector } from "utils/redux";
import { getEmployerForm } from "forms/employer";

import EmployerPageContainer from "./EmployerPageContainer";
import KaboodleForm from "components/Forms/KaboodleForm";

import { setIsLoading } from "reducers/main";
import { useDispatch } from "utils/redux";

const EmployerForm = () => {
  const dispatch = useDispatch();
  const { employerId } = useParams<{ employerId?: string }>();

  const {
    data: employer,
    isLoading,
    refetch: refetchEmployer,
  } = useGetEmployerByIdQuery({ id: employerId }, { skip: !employerId });

  useEffect(() => {
    dispatch(setIsLoading(isLoading));
  }, [dispatch, isLoading]);

  const [patchEmployerById] = usePatchEmployerByIdMutation();

  const { reset, clearErrors, setError } = useFormContext();

  const constants = useSelector((state) => state.shared.constants);
  const loggedInUser = useSelector((state) => state.main.loggedInUser);
  const isReadOnly = !loggedInUser?.has_operations_authority;

  useEffect(() => {
    if (employer) {
      reset(employer);
    }
    refetchEmployer();
  }, [employer, reset]);

  const handleSave = useCallback(
    async (field: string, value: string | number) => {
      if (employerId) {
        try {
          await patchEmployerById({
            id: employerId,
            data: { [field]: value },
          }).unwrap();
          clearErrors(field);
        } catch (error) {
          setError(field, {
            type: error.status,
            message: error.data[field][0],
          });
        }
      }
    },
    [patchEmployerById, employerId, setError],
  );

  const employerFormContent = useMemo(() => {
    const forms = getEmployerForm(constants, isReadOnly);
    return <KaboodleForm cards={forms[0].cards || []} onSave={handleSave} />;
  }, [constants, handleSave, isReadOnly]);

  return <EmployerPageContainer>{employerFormContent}</EmployerPageContainer>;
};

export default EmployerForm;
